import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { AnalysisResultsDisplayProps, Topic } from './types';

// Import components
import AnalysisSummary from './components/AnalysisSummary';
import TopicCard from './components/TopicCard';
import FeedbackSection from './components/FeedbackSection';
import { useToast } from '@/context/ToastContext';
import { useApp } from '@/context/AppContext';

export const AnalysisResultsDisplay: React.FC<AnalysisResultsDisplayProps> = ({ 
  data, 
  workflowId, 
  onApproved,
  onReprocess,
  isProcessing,
  serviceAvailable,
  onResultsLoaded
}) => {
  const [tabValue, setTabValue] = useState(0);
  const theme = useTheme();
  const { showToast } = useToast();
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { updateWorkflowData } = useApp();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Navigation handler to Module 2
  const navigateToModule2 = () => {
    if (!workflowId) {
      showToast('No workflow ID available', 'error');
      return;
    }
    
    // Debug logging to understand the data structure
    console.log('AnalysisResults data:', data);
    console.log('AnalysisResults data type:', typeof data);
    console.log('AnalysisResults data has output_blob:', data && 'output_blob' in data);
    if (data && data.output_blob) {
      console.log('output_blob value:', data.output_blob);
    } else {
      console.log('output_blob is missing or undefined');
      
      // Check if there are any likely path fields
      const possiblePathFields = Object.keys(data || {}).filter(key => 
        typeof data[key] === 'string' && 
        (key.includes('path') || key.includes('blob') || key.includes('file') || data[key].includes('/'))
      );
      
      if (possiblePathFields.length > 0) {
        console.log('Possible alternative path fields:', possiblePathFields);
        possiblePathFields.forEach(field => {
          console.log(`Field ${field} value:`, data[field]);
        });
      }
    }
    
    // Extract workflow path if available
    let workflowPath = null;
    
    // Extract client code and external ID if available
    let clientCode = clientId || null;
    let externalId = workflowId || null;
    
    // If data has client_code or external_id, use those
    if (data) {
      if (data.client_code) clientCode = data.client_code;
      if (data.external_id) externalId = data.external_id;
      if (data.client_id && !clientCode) clientCode = data.client_id;
      if (data.workflow_id && !externalId) externalId = data.workflow_id;
    }
    
    console.log('Extracted clientCode:', clientCode);
    console.log('Extracted externalId:', externalId);
    
    if (data && data.output_blob) {
      // Extract workflow path from output_blob as done in module1Service.ts
      const pathParts = data.output_blob.split('/');
      const workflowsIndex = pathParts.findIndex((part: string) => part === 'workflows');
      
      if (workflowsIndex >= 0 && workflowsIndex + 1 < pathParts.length) {
        const workflowIdFromPath = pathParts[workflowsIndex + 1];
        workflowPath = pathParts.slice(0, workflowsIndex + 1).join('/') + '/' + workflowIdFromPath + '/outputs/module1';
        console.log(`Extracted workflow path: ${workflowPath}`);
        
        // If we found a workflow ID in the path, use it as external ID
        if (workflowIdFromPath && !externalId) {
          externalId = workflowIdFromPath;
          console.log(`Updated externalId from path: ${externalId}`);
        }
      } else {
        console.log('Could not extract workflow path - workflows part not found in path:', pathParts);
      }
    }
    
    // Log the data before and after we try to set it to context
    console.log('Workflow path extracted:', workflowPath);
    console.log('Client code and external ID:', clientCode, externalId);
    
    // Update the context with all available information
    updateWorkflowData({
      workflowPath: workflowPath,
      clientCode: clientCode,
      externalId: externalId,
      // Keep other existing data
      ...data
    });
    
    // Navigate to Module 2
    if (clientId) {
      navigate(`/module2/client/${clientId}/${workflowId}`);
    } else {
      navigate(`/module2?workflowId=${workflowId}`);
    }
  };

  if (!data) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
        <CircularProgress size={40} />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading analysis results...</Typography>
      </Box>
    );
  }

  // Handle potential nested structures - the data sometimes has both root-level topics and results.topics
  let effectiveData = data;
  
  // First check: Is our data nested inside a results property?
  if (data.results && data.results.topics && Array.isArray(data.results.topics)) {
    effectiveData = data.results;
  }

  // Check if data is nested in a content, results, or draft_results property
  // Try several levels of nesting as sometimes the results come back in complex objects
  let actualData = {...effectiveData}; // Make a copy to avoid reference issues
  
  // Process various possible nesting patterns
  if (data.content && typeof data.content === 'object') {
    console.log('Found data.content, using it');
    actualData = data.content;
  } else if (data.results && typeof data.results === 'object' && !actualData.topics) {
    console.log('Found data.results, using it');
    actualData = data.results;
  } else if (data.draft_results && typeof data.draft_results === 'object') {
    console.log('Found data.draft_results, using it');
    actualData = data.draft_results;
  }
  
  // Handle multiple levels of nesting - sometimes the structure is results.content
  if (actualData.content && typeof actualData.content === 'object') {
    console.log('Found actualData.content, using it');
    actualData = actualData.content;
  }
  
  // Make sure subtopics have both stem_words and keywords arrays
  if (actualData.topics && Array.isArray(actualData.topics)) {
    actualData.topics.forEach((topic: any) => {
      if (topic.subtopics && Array.isArray(topic.subtopics)) {
        topic.subtopics.forEach((subtopic: any) => {
          // Ensure stem_words exists
          if (!subtopic.stem_words) {
            subtopic.stem_words = [];
          } else if (!Array.isArray(subtopic.stem_words)) {
            subtopic.stem_words = [String(subtopic.stem_words)];
          }
          
          // Ensure keywords exists
          if (!subtopic.keywords) {
            // If we have stem_words but no keywords, copy stem_words to keywords
            if (Array.isArray(subtopic.stem_words) && subtopic.stem_words.length > 0) {
              subtopic.keywords = [...subtopic.stem_words];
            } else {
              subtopic.keywords = [];
            }
          } else if (!Array.isArray(subtopic.keywords)) {
            subtopic.keywords = [String(subtopic.keywords)];
          }
        });
      }
    });
  }
  
  // More robust format detection
  const isArrayFormat = actualData.topics && Array.isArray(actualData.topics);
  const isObjectFormat = actualData.topics && typeof actualData.topics === 'object' && !Array.isArray(actualData.topics);
  
  // Different variables for clarity (isOriginalFormat was confusing)
  const topics = isArrayFormat ? actualData.topics : [];

  return (
    <Box sx={{ width: '100%' }}>
      {/* Summary Card */}
      <AnalysisSummary topicsCount={topics.length} workflowId={workflowId} />

      {/* Topics & Subtopics */}
      <Box>
        <Typography variant="h6" gutterBottom>
          Identified Topics & Subtopics
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          The analysis identified the following topics and subtopics based on your keywords.
        </Typography>
        
        {isArrayFormat ? (
          // Array format (new format) with topics and subtopics as arrays
          topics.length > 0 ? (
            <Grid container spacing={3}>
              {topics.map((topic: Topic, topicIndex: number) => (
                <TopicCard 
                  key={topicIndex}
                  topic={topic}
                  topicIndex={topicIndex}
                />
              ))}
            </Grid>
          ) : (
            <Alert severity="info" sx={{ mt: 2 }}>
              No topics have been identified yet
            </Alert>
          )
        ) : isObjectFormat ? (
          // Object format (old format) with topics as object keys
          <Grid container spacing={3}>
            {Object.entries(data.topics).map(([topicName, topicData]: [string, any], index: number) => (
              <Grid item xs={12} md={6} key={index}>
                <TopicCard 
                  topic={{
                    name: topicName,
                    subtopics: Object.entries(topicData.subtopics || {}).map(([subtopicName, stemWords]) => ({
                      name: subtopicName,
                      stem_words: Array.isArray(stemWords) ? stemWords : [],
                      keywords: Array.isArray(stemWords) ? stemWords : []
                    }))
                  }}
                  topicIndex={index}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          // No recognized format
          <Alert severity="warning" sx={{ mt: 2 }}>
            The data format could not be recognized. Please check the console for debug information.
          </Alert>
        )}
      </Box>

      {/* Verification and Feedback Section */}
      {workflowId && (
        <FeedbackSection 
          workflowId={workflowId}
          onApproved={onApproved}
          onReprocess={onReprocess}
          isProcessing={isProcessing}
          serviceAvailable={serviceAvailable}
          onResultsLoaded={onResultsLoaded}
        />
      )}
    </Box>
  );
};

export default AnalysisResultsDisplay;
